import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { Selector, useSelector } from 'react-redux';

import { PosPlugin } from 'plugins/plugin';
import { GivexCard } from 'plugins/givexHeartland/types';
import {
  getDefaultCurrency,
  getPosRoundCashFunction,
} from 'reducers/configs/settings';
import { getPluginHooksByName } from 'reducers/Plugins';
import { RootState } from 'reducers';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { getTotal } from 'reducers/ShoppingCart';
import { getGivexConfiguration } from 'plugins/givexHeartland/configuration/Configuration';
import { reactcursively } from 'utils/react';
import {
  EOD_TENDER_GIVEX_HEADER_TEST_KEY,
  EOD_TENDER_GIVEX_HEADER_TESTID,
} from 'plugins/mm/constants';
import { getPayments } from 'reducers/Payments';

const isHeader = R.pathEq(
  ['props', 'data-testid'],
  EOD_TENDER_GIVEX_HEADER_TESTID,
);
const isGivex = R.pathEq(
  ['props', 'data-test-key'],
  EOD_TENDER_GIVEX_HEADER_TEST_KEY,
);

const isGivexHeader = R.allPass([isHeader, isGivex]);

const getGivexCardToDeactivate = (state: RootState) => {
  const customHooks = getPluginHooksByName<Record<string, any>>('customHooks')(
    state,
  );
  const hook = customHooks.find(ch => !!ch?.getGivexCardToDeactivate) as
    | {
        getGivexCardToDeactivate: Selector<
          RootState,
          GivexCard & {
            balance: string;
          }
        >;
      }
    | undefined;

  if (!hook) return null;

  return hook.getGivexCardToDeactivate(state);
};

const GIVEX_DEACTIVATION_REFUND = 'mm-givex-deactivation-refund';

export const setReturnPaymentForGivexCardDeactivation: Required<
  PosPlugin
>['onOpenPaymentModal']['on'] = (p, ap) => async (dispatch, getState) => {
  const givexCardToDeactivate = getGivexCardToDeactivate(getState());
  if (!givexCardToDeactivate) return ap;
  const existingReturnPayment = ap.payments[GIVEX_DEACTIVATION_REFUND];
  if (existingReturnPayment) return ap;

  const currencyCode = getDefaultCurrency(getState());
  const paymentType =
    Number(givexCardToDeactivate.balance) <= 5 ? 'CASH' : 'CARD';
  const newPayment = {
    key: GIVEX_DEACTIVATION_REFUND,
    locked: true,
    amount: `-${givexCardToDeactivate.balance}`,
    type: paymentType,
    caption: paymentType,
    currencyCode,
    currencyRate: 1,
  };

  return R.evolve({ payments: R.assoc(newPayment.key, newPayment) })(ap);
};

function isCashReturnPayment(payment) {
  return payment.type === 'CASH';
}

const debitCardTypes = {
  Debit: true,
  'UnionPay Debit': true,
};

function isCreditPayment(payment) {
  return payment.type === 'CARD' && !debitCardTypes[payment.cardType];
}
export const setReturnPaymentsToGivex: Required<
  PosPlugin
>['onOpenPaymentModal']['on'] = (p, ap) => async (dispatch, getState) => {
  const currentWarehouseID = getSelectedWarehouseID(getState());
  const roundCash = getPosRoundCashFunction(getState());
  const originalSaleLocation = ap.currentSalesDocument?.warehouseID;
  const total = getTotal(getState());
  const { displayName: caption } = getGivexConfiguration(getState());

  // If is not a return, do not do any edits to the prefilled tenders
  if (total >= 0) return ap;

  const givexFullPayment = {
    produuid: uuidv4(),
    locked: true,
    type: 'CARD',
    paymentIntegration: 'givex',
    cardType: 'GIVEX',
    amount: ap.total.toFixed(2),
    caption,
  };

  const isReferencedReturn = (ap?.originalPayments ?? []).length > 0;

  // When making unreferenced return, ALWAYS return to GIVEX
  if (!isReferencedReturn) {
    return {
      ...ap,
      paymentLimits: [{ type: 'CARD', amount: Math.abs(ap.total) }],
      payments: {
        [givexFullPayment.produuid]: givexFullPayment,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const paymentList = ap.originalPayments!; // isReferencedReturn ensures array exists

  const wasPaidBySingleTender = ap.originalPayments?.length === 1;

  const isSameLocation = originalSaleLocation === Number(currentWarehouseID);

  if (isSameLocation && wasPaidBySingleTender) {
    const [payment] = paymentList;

    if (isCreditPayment(payment) || isCashReturnPayment(payment)) {
      const [prefillPayment] = Object.values(ap.payments) as any[];
      // Safe-guard: Not pre-filled by core POS
      if (!prefillPayment) {
        return ap;
      }
      const rounded = roundCash(ap.total, undefined);
      const limit = prefillPayment.type === 'CASH' ? rounded : ap.total;
      return {
        ...ap,
        paymentLimits: [
          // Apply a limit so that only the matching tender is allowed up to the sale total
          { type: prefillPayment.type, amount: Math.abs(limit) },
        ],
        // Mark whichever single payment we have as 'locked' so that the user has no way of removing it
        payments: {
          [Object.keys(ap.payments)[0]]: {
            ...prefillPayment,
            locked: true,
          },
        },
      };
    }
  }

  return {
    ...ap,
    // Limit to CARD for maximum of the cart total
    paymentLimits: [{ type: 'CARD', amount: Math.abs(ap.total) }],
    payments: {
      [givexFullPayment.produuid]: givexFullPayment,
    },
  };
};

export const WithoutPaymentsBar: PosPlugin['UIPaymentActions'] = ({
  children,
}) => {
  const total = useSelector(getTotal);
  if (total >= 0) {
    return children;
  }
  return reactcursively(
    R.when(
      R.pathEq(['props', 'data-testid'], 'payment-actions-desktop'),
      () => null,
    ),
  )(children);
};

export const WithGivexRenamed: PosPlugin['UITenderTable'] = ({ children }) => {
  const { displayName: caption } = useSelector(getGivexConfiguration);
  return reactcursively(
    R.when(isGivexHeader, R.assocPath(['props', 'children'], caption)),
  )(children);
};
